/**
 * TRUSTGATE_LOCAL_AUTH_INITIALIZATION_GUARD_V1
 * 
 * Visible error panel for local dev when Firebase Auth is not initialized.
 * Shows clear remediation instructions and prevents silent fallback.
 */

import React from 'react';

interface AuthInitializationErrorPanelProps {
  errorMessage: string;
}

export const AuthInitializationErrorPanel: React.FC<AuthInitializationErrorPanelProps> = ({ errorMessage }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#16213e] flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-[#1a1a2e] border border-red-500/50 rounded-lg p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-red-400">TrustGate Auth Initialization Failed</h1>
        </div>

        <div className="bg-[#0a0a0f] rounded-lg p-4 mb-6 border border-red-500/30">
          <p className="text-[#cbd5e1] whitespace-pre-wrap font-mono text-sm leading-relaxed">
            {errorMessage}
          </p>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h2 className="text-sm font-semibold text-blue-400 mb-2">Quick Fix</h2>
          <ol className="text-[#cbd5e1] text-sm space-y-2 list-decimal list-inside">
            <li>Check your <code className="bg-[#0a0a0f] px-2 py-1 rounded text-blue-300">.env</code> file exists in the project root</li>
            <li>Ensure all <code className="bg-[#0a0a0f] px-2 py-1 rounded text-blue-300">VITE_FIREBASE_*</code> variables are set</li>
            <li>Restart the dev server: <code className="bg-[#0a0a0f] px-2 py-1 rounded text-blue-300">Ctrl+C</code> then <code className="bg-[#0a0a0f] px-2 py-1 rounded text-blue-300">npm run dev</code></li>
          </ol>
        </div>

        <div className="mt-6 text-center">
          <p className="text-[#64748b] text-sm">
            Need help? Check the browser console for detailed error messages.
          </p>
        </div>
      </div>
    </div>
  );
};
